import { useEffect, useState } from "react";
import BulkUploadButton from "../../components/BulkUploadButton";
import DataExportButton from "../../components/DataExportButton";
import {
  Alert,
  Box,
  Button,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useSearchParams } from "react-router-dom";

import {
  AppDeleteDialog,
  AppPageHeader,
  AppSearchField,
  AppToolbar,
} from "../../../../components/common";
import { CategoryService } from "../../categories/services/category.service";
import { Category } from "../../categories/types";

import SparePartFormDialog from "../components/SparePartFormDialog";
import SparePartDetailsDialog from "../components/SparePartDetailsDialog";
import SparePartTable from "../components/SparePartTable";

import { useSpareParts } from "../hooks/useSpareParts";

import {
  SparePart,
  SparePartPayload,
} from "../types";
import { filterRowsBySearch } from "../../utils/rowSearch";

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const SparePartsPage = () => {
  const {
    spareParts,
    loading,
    error,
    pagination,
    filters,
    fetchSpareParts,
    createSparePart,
    updateSparePart,
    deleteSparePart,
    updateStatus,
    updateFilters,
  } = useSpareParts();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] =
    useState(false);

  const [detailsOpen, setDetailsOpen] =
    useState(false);

  const [selectedPart, setSelectedPart] =
    useState<SparePart | null>(null);

  const [saving, setSaving] =
    useState(false);

  const [deleteOpen, setDeleteOpen] =
    useState(false);

  const [deleteLoading, setDeleteLoading] =
    useState(false);

  const [partToDelete, setPartToDelete] =
    useState<SparePart | null>(null);

  const [categories, setCategories] = useState<Category[]>([]);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    CategoryService.getCategories({
      page: 1,
      limit: 100,
      status: "ACTIVE",
      sortBy: "displayOrder",
      sortOrder: "asc",
    })
      .then((response) =>
        setCategories(
          response.data.filter((category) => UUID_REGEX.test(category.id))
        )
      )
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (searchParams.get("create") === "1") {
      setSelectedPart(null);
      setDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCreate = () => {
    setSelectedPart(null);
    setSubmitError("");
    setDialogOpen(true);
  };

  const handleEdit = (
    sparePart: SparePart
  ) => {
    setSelectedPart(sparePart);
    setSubmitError("");
    setDialogOpen(true);
  };

  const handleView = (
    sparePart: SparePart
  ) => {
    setSelectedPart(sparePart);
    setDetailsOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedPart(null);
    setSubmitError("");
  };

  const handleSubmit = async (
    payload: SparePartPayload
  ) => {
    try {
      setSaving(true);
      setSubmitError("");

      if (selectedPart) {
        await updateSparePart(
          selectedPart.id,
          payload
        );
      } else {
        await createSparePart(payload);
      }

      handleClose();
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      const firstError =
        Array.isArray(errors) && errors[0]
          ? `${errors[0]?.path?.join?.(".") || "Field"}: ${errors[0]?.message}`
          : "";
      const message =
        firstError ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Unable to save spare part.";

      setSubmitError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (
    sparePart: SparePart
  ) => {
    setPartToDelete(sparePart);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!partToDelete) return;

    try {
      setDeleteLoading(true);

      await deleteSparePart(
        partToDelete.id
      );

      setDeleteOpen(false);
      setPartToDelete(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSearchChange = (
    value: string
  ) => {
    setSearch(value);

    updateFilters({
      search: value,
      page: 1,
    });
  };

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "calc(100vh - 96px)",
        borderRadius: 3,
        bgcolor: "background.paper",
      }}
    >
      <Stack spacing={3}>
        <AppPageHeader
          title="Spare Parts"
          subtitle="Manage spare parts master data."
          action={
            <Stack direction="row" flexWrap="wrap" gap={1}>
            <BulkUploadButton kind="spare-parts" onImported={fetchSpareParts} />
            <DataExportButton kind="spare-parts" filters={filters} disabled={loading} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Create Spare Part
            </Button>
            </Stack>
          }
        />

        {error && (
          <Alert
            severity="error"
            onClose={fetchSpareParts}
          >
            {error}
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError("")}>
            {submitError}
          </Alert>
        )}

        <AppToolbar
          left={
            <AppSearchField
              value={search}
              fullWidth
              placeholder="Search spare parts..."
              onChange={
                handleSearchChange
              }
            />
          }
        />

        <SparePartTable
          rows={filterRowsBySearch(spareParts, search)}
          loading={loading}
          rowCount={pagination.total}
          page={filters.page - 1}
          pageSize={filters.limit}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onStatusChange={(
            sparePart,
            checked
          ) =>
            updateStatus(
              sparePart.id,
              checked
                ? "ACTIVE"
                : "INACTIVE"
            )
          }
          onPageChange={(page) =>
            updateFilters({
              page: page + 1,
            })
          }
          onPageSizeChange={(
            pageSize
          ) =>
            updateFilters({
              page: 1,
              limit: pageSize,
            })
          }
        />
      </Stack>

      <SparePartFormDialog
        open={dialogOpen}
        loading={saving}
        sparePart={selectedPart}
        categories={categories}
        error={submitError}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />

      <SparePartDetailsDialog
        open={detailsOpen}
        sparePart={selectedPart}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedPart(null);
        }}
      />

      <AppDeleteDialog
        open={deleteOpen}
        loading={deleteLoading}
        title="Delete Spare Part"
        content={`Are you sure you want to delete ${
          partToDelete?.name ??
          "this spare part"
        }?`}
        onClose={() => {
          setDeleteOpen(false);
          setPartToDelete(null);
        }}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default SparePartsPage;
